import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { prayerLabel } from '@/shared/lib/labels';
import type { CoverageStatus, PrayerSlot } from '@/shared/model/enums';
import { MatchStrengthMeter } from '@/shared/ui/MatchStrengthMeter';
import { CoverageStatusChip } from '@/shared/ui/StatusChip';
import { useUiStore } from '@/stores/ui.store';

import { coverageDatesText } from './coverage-format';

/** The fields a summary card needs; satisfied by CoverageRequest and MatchedRequest. */
export interface RequestSummary {
  readonly masjidName: string | null;
  readonly masjidCity: string | null;
  readonly status: CoverageStatus;
  readonly prayerSlots: readonly PrayerSlot[];
  readonly startDate: string;
  readonly endDate: string;
}

/**
 * Summary card for a coverage request; used in browse, matched-feed, and my-requests.
 * On the freelancer feed it also carries a match-strength meter (`matchScore`) and,
 * for the top-ranked open request, a "best for you" pill (`topMatch`). A request that
 * includes Jumu'ah is visually distinct everywhere (gold strip + gold slot chip) so
 * the khutbah requirement reads at a glance — never by color alone (the chip is
 * labeled "خطبة").
 */
export function RequestCard({
  request,
  distanceKm,
  matchScore,
  topMatch = false,
  onOpen,
}: {
  request: RequestSummary;
  distanceKm?: number | null;
  matchScore?: number | null;
  topMatch?: boolean;
  onOpen: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const locale = useUiStore((s) => s.locale);
  const hasJumuah = request.prayerSlots.includes('jumuah');

  const classes = [
    'card',
    'card--interactive',
    'stack',
    topMatch ? 'card--top-match' : '',
    hasJumuah ? 'card--jumuah' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type="button" className={classes} onClick={onOpen}>
      {topMatch ? (
        <span className="pill-best">
          <Star size={14} fill="currentColor" />
          {t('match.bestForYou')}
        </span>
      ) : null}
      <div className="row row--between">
        <strong>{request.masjidName ?? t('masjid.fallbackName')}</strong>
        <CoverageStatusChip status={request.status} />
      </div>
      {request.masjidCity ? <span className="muted">{request.masjidCity}</span> : null}
      <span>{coverageDatesText(locale, request)}</span>
      <div className="wrap">
        {request.prayerSlots.map((prayer) => (
          <span
            key={prayer}
            className={`badge${prayer === 'jumuah' ? ' badge--jumuah' : ''}`}
            style={prayer === 'jumuah' ? undefined : { background: 'var(--color-surface-2)' }}
          >
            {prayerLabel(t, prayer)}
          </span>
        ))}
      </div>
      {matchScore != null || distanceKm != null ? (
        <div className="row row--between">
          {matchScore != null ? <MatchStrengthMeter score={matchScore} /> : <span />}
          {distanceKm != null ? (
            <span className="muted">{t('match.distanceKm', { km: distanceKm })}</span>
          ) : null}
        </div>
      ) : null}
    </button>
  );
}
